import { supabase } from '../supabase';
import { currentDivisionId } from '../stores/useFiltersStore';

export const SHIPMENT_CASES_CHANGED_EVENT = 'shipment-cases-changed';

export function notifyShipmentCasesChanged(exporterId: number) {
  if (typeof window !== 'undefined')
    window.dispatchEvent(
      new CustomEvent(SHIPMENT_CASES_CHANGED_EVENT, { detail: { exporterId } })
    );
}

export interface CaseScope {
  exporter_id: number;
  season: string;
  species_id: number;
}
export type CaseKind = 'observation' | 'claim' | 'incident';
export type CaseStatus = 'internal' | 'waiting' | 'closed';
export type CaseOwner =
  | { kind: 'user'; id: number; name?: string }
  | { kind: 'area'; name: string };
export interface CaseFile {
  name: string;
  size: number;
  type: string;
}
export interface CaseItem {
  source_box_id: number;
  kilos: number;
  snapshot: {
    pallet?: string;
    variety: string;
    caliber?: string;
    quantity: number;
    box_weight: number;
    producer?: string;
  };
}
export interface CaseValuation {
  id: number;
  event_id: number;
  kilos: number;
  benchmark_usd_kg: number;
  obtained_usd_kg: number;
  amount_usd: number;
  confidence: 'firm' | 'solid' | 'estimated' | 'reference';
  methodology: string;
  sources: { benchmark: string; obtained: string; evidence: string };
  created_at: string;
}
export interface ShipmentCase extends CaseScope {
  id: number;
  shipment_id: number | null;
  case_number: string | null;
  current_reference: string;
  shipment_snapshot: {
    id: number;
    reference: string;
    container?: string;
    receiver?: string;
    vessel?: string;
  };
  kind: CaseKind;
  status: CaseStatus;
  reasons: string[];
  description: string;
  /** Historical case assignment; new work is assigned through requests. */
  owner: CaseOwner | null;
  responsible: string | null;
  responsible_name: string | null;
  created_at: string;
  updated_at: string;
  version: number;
  closed_at: string | null;
  outcome: 'resolved' | 'discarded' | 'rejected' | null;
  closure_note: string | null;
  pending_inputs: number;
  input_due_on: string | null;
  oldest_input: {
    recipient: string;
    requested_at: string;
    due_on: string;
  } | null;
  waiting_parties: string[];
  pending_requests: CaseInput[];
  species_name?: string;
  valuation: CaseValuation | null;
  items: CaseItem[];
}
export interface CaseInput {
  id: number;
  case_id: number;
  recipient_kind: 'internal' | 'external';
  recipient: string;
  recipient_owner?: CaseOwner | null;
  requested: string;
  due_on: string;
  requested_at: string;
  received_at: string | null;
  response: string | null;
  external_entity_id?: number | null;
  external_entity?: { id: number; name: string; type: string } | null;
  followup_owner?: CaseOwner | null;
}
export interface CaseEvent {
  id: number;
  actor_name: string;
  action: string;
  created_at: string;
  files: CaseFile[];
  payload: {
    before: ShipmentCase | null;
    after: ShipmentCase;
    details: CaseAction;
    input_id: number | null;
  };
}
export interface CaseDetail {
  events: CaseEvent[];
  inputs: CaseInput[];
  valuations: CaseValuation[];
  documents: { event_id: number; file_index: number; path: string }[];
}
export interface CaseCargo {
  id: number;
  pallet: string | null;
  variety: string;
  caliber: string | null;
  quantity: number;
  box_weight: number;
  kilos: number;
  /** Labels of open cases on this line: the custom case number or CR-<id>. */
  active_cases: string[];
}
export interface CaseOptions {
  people: { id: number; name: string }[];
  shipments: {
    id: number;
    reference_number: string;
    container_number: string | null;
    receiver: string | null;
  }[];
  cargo: CaseCargo[];
}
export interface CaseAction {
  action:
    | 'create'
    | 'note'
    | 'assign'
    | 'responsible'
    | 'escalate'
    | 'request_input'
    | 'receive_input'
    | 'assign_input'
    | 'value'
    | 'close'
    | 'reopen';
  case_id?: number;
  version?: number;
  shipment_id?: number;
  owner?: CaseOwner;
  reasons?: string[];
  box_ids?: number[];
  responsible?: string;
  responsible_name?: string;
  case_number?: string;
  initial_request?: {
    recipient_owner: CaseOwner;
    note: string;
    due_days: number;
  };
  note?: string;
  kind?: CaseKind;
  recipient_kind?: 'internal' | 'external';
  recipient?: string;
  recipient_owner?: CaseOwner;
  external_entity_id?: number;
  external_entity?: CaseInput['external_entity'];
  followup_owner?: CaseOwner;
  previous_followup_owner?: CaseOwner | null;
  due_days?: number;
  input_id?: number;
  outcome?: 'resolved' | 'discarded' | 'rejected';
  benchmark_usd_kg?: number;
  obtained_usd_kg?: number;
  benchmark_source?: string;
  obtained_source?: string;
  methodology?: string;
}
export interface CaseCommandResult {
  case_id: number;
  event_id: number;
  version: number;
}
const scopeArgs = (s: CaseScope) => ({
  p_exporter: s.exporter_id,
  p_season: s.season,
  p_species: s.species_id,
});

export interface CaseReason {
  id: number;
  name: string;
  active: boolean;
  version: number;
}
export interface CaseExternalEntity {
  id: number;
  name: string;
  entity_type: string;
  active: boolean;
  version: number;
}
export interface CaseEntityCatalog {
  entities: CaseExternalEntity[];
  types: string[];
}
export async function getCaseExternalEntities(
  exporterId: number,
  signal: AbortSignal
): Promise<CaseEntityCatalog> {
  const { data, error } = await supabase
    .rpc('get_shipment_case_external_entities', { p_exporter: exporterId })
    .abortSignal(signal);
  if (error) throw error;
  if (!data || !Array.isArray(data.entities) || !Array.isArray(data.types))
    throw new Error('Invalid entity catalog');
  return data as CaseEntityCatalog;
}
export async function saveCaseExternalEntity(
  exporterId: number,
  name: string,
  entityType: string,
  existing?: CaseExternalEntity,
  active = true
): Promise<CaseExternalEntity> {
  const { data, error } = await supabase.rpc(
    'save_shipment_case_external_entity',
    {
      p_exporter: exporterId,
      p_name: name,
      p_type: entityType,
      p_id: existing?.id ?? null,
      p_version: existing?.version ?? null,
      p_active: active,
    }
  );
  if (error) throw error;
  return data as CaseExternalEntity;
}
export async function getCaseReasons(
  exporterId: number,
  signal: AbortSignal
): Promise<CaseReason[]> {
  const { data, error } = await supabase
    .rpc('get_shipment_case_reasons', { p_exporter: exporterId })
    .abortSignal(signal);
  if (error) throw error;
  if (!Array.isArray(data)) throw new Error('Invalid reasons response');
  return data as CaseReason[];
}
export async function saveCaseReason(
  exporterId: number,
  name: string,
  existing?: CaseReason,
  active = true
): Promise<CaseReason> {
  const { data, error } = await supabase.rpc('save_shipment_case_reason', {
    p_exporter: exporterId,
    p_name: name,
    p_id: existing?.id ?? null,
    p_version: existing?.version ?? null,
    p_active: active,
  });
  if (error) throw error;
  return data as CaseReason;
}

export async function getShipmentCases(
  s: CaseScope,
  signal: AbortSignal
): Promise<ShipmentCase[]> {
  return readCasePages(
    'get_shipment_cases',
    { ...scopeArgs(s), p_division_id: currentDivisionId() },
    signal
  );
}
export async function getMyShipmentCases(
  exporterId: number,
  signal: AbortSignal
): Promise<ShipmentCase[]> {
  return readCasePages(
    'get_my_shipment_cases',
    { p_exporter: exporterId, p_division_id: currentDivisionId() },
    signal
  );
}
async function readCasePages(
  rpc: 'get_shipment_cases' | 'get_my_shipment_cases',
  args: Record<string, unknown>,
  signal: AbortSignal
): Promise<ShipmentCase[]> {
  const rows: ShipmentCase[] = [];
  let before: number | null = null;
  for (;;) {
    const { data, error } = await supabase
      .rpc(rpc, { ...args, p_before: before })
      .abortSignal(signal);
    if (error) throw error;
    if (!Array.isArray(data)) throw new Error('Invalid cases response');
    rows.push(...(data as ShipmentCase[]));
    if (data.length < 100) return rows;
    const next: number = Number(data[data.length - 1].id);
    if (
      !Number.isSafeInteger(next) ||
      next <= 0 ||
      (before !== null && next >= before)
    )
      throw new Error('Invalid cases cursor');
    before = next;
  }
}
export async function getShipmentCaseRecord(
  s: CaseScope,
  id: number,
  signal: AbortSignal
): Promise<ShipmentCase> {
  const { data, error } = await supabase
    .rpc('get_shipment_case_record', { ...scopeArgs(s), p_id: id })
    .abortSignal(signal);
  if (error) throw error;
  if (!data || data.id !== id) throw new Error('Invalid case response');
  return data as ShipmentCase;
}
export async function getShipmentCaseDetail(
  s: CaseScope,
  id: number,
  signal: AbortSignal
): Promise<CaseDetail> {
  const { data, error } = await supabase
    .rpc('get_shipment_case_detail', { ...scopeArgs(s), p_id: id })
    .abortSignal(signal);
  if (error) throw error;
  if (
    !data ||
    !Array.isArray(data.events) ||
    !Array.isArray(data.inputs) ||
    !Array.isArray(data.documents) ||
    !Array.isArray(data.valuations)
  )
    throw new Error('Invalid case detail');
  return data as CaseDetail;
}
export async function getShipmentCaseOptions(
  s: CaseScope,
  search: string,
  shipment: number | null,
  signal: AbortSignal
): Promise<CaseOptions> {
  const { data, error } = await supabase
    .rpc('get_shipment_case_options', {
      ...scopeArgs(s),
      p_search: search,
      p_shipment: shipment,
      p_division_id: currentDivisionId(),
    })
    .abortSignal(signal);
  if (error) throw error;
  if (
    !data ||
    !Array.isArray(data.people) ||
    !Array.isArray(data.shipments) ||
    !Array.isArray(data.cargo)
  )
    throw new Error('Invalid case options');
  return data as CaseOptions;
}
export interface CaseIntent {
  requestId: string;
  body: CaseScope & CaseAction & { files: CaseFile[] };
  files: File[];
  result?: CaseCommandResult;
  uploaded: Set<number>;
}
export function createCaseIntent(
  scope: CaseScope,
  action: CaseAction,
  files: File[]
): CaseIntent {
  if (
    files.length > 10 ||
    files.some(
      (f) =>
        !f.size || f.size > 10485760 || f.name.length > 200 || !caseFileType(f)
    )
  )
    throw new Error(
      'Selecciona hasta diez documentos PDF, imágenes, Word, Excel, TXT o EML; máximo 10 MB por archivo.'
    );
  return {
    requestId: crypto.randomUUID(),
    body: {
      exporter_id: scope.exporter_id,
      season: scope.season,
      species_id: scope.species_id,
      ...structuredClone(action),
      files: files.map((f) => ({
        name: f.name,
        size: f.size,
        type: caseFileType(f)!,
      })),
    },
    files: [...files],
    uploaded: new Set(),
  };
}
function caseFileType(file: File): string | undefined {
  const types: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    txt: 'text/plain',
    eml: 'message/rfc822',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };
  return types[file.name.split('.').pop()?.toLowerCase() || ''];
}
/** The same body/UUID and files survive an uncertain response or partial upload. */
export async function executeCaseIntent(
  intent: CaseIntent
): Promise<CaseCommandResult> {
  if (!intent.result) {
    const { data, error } = await supabase.rpc('command_shipment_case', {
      p_request: intent.requestId,
      p_body: intent.body,
    });
    if (error) throw error;
    if (
      !data ||
      !Number.isSafeInteger(data.case_id) ||
      !Number.isSafeInteger(data.event_id)
    )
      throw new Error('Invalid command result');
    intent.result = data as CaseCommandResult;
    notifyShipmentCasesChanged(intent.body.exporter_id);
    void deliverCaseNotifications();
  }
  for (let i = 0; i < intent.files.length; i++) {
    if (intent.uploaded.has(i)) continue;
    const path = `${intent.body.exporter_id}/${intent.result.case_id}/${intent.result.event_id}/${i}`;
    const file = intent.files[i];
    await supabase.storage.from('shipment-case-documents').upload(path, file, {
      upsert: false,
      contentType: intent.body.files[i].type,
    });
    // Confirming an existing object recovers duplicate/lost upload responses.
    // A storage error alone never marks a file as uploaded.
    const { error } = await supabase.rpc('confirm_shipment_case_file', {
      p_path: path,
    });
    if (error) throw error;
    intent.uploaded.add(i);
  }
  return intent.result;
}
/**
 * Pide al backend que envíe los correos pendientes de la exportadora del
 * usuario (solicitudes creadas, respondidas o reasignadas). Nunca bloquea ni
 * hace fallar la acción que lo dispara.
 */
export async function deliverCaseNotifications(): Promise<void> {
  const functions = (
    supabase as unknown as {
      functions?: {
        invoke: (
          name: string,
          options: { body: Record<string, never> }
        ) => Promise<{ error: unknown }>;
      };
    }
  ).functions;
  if (!functions) return;
  try {
    const { error } = await functions.invoke('notify-shipment-cases', {
      body: {},
    });
    if (error)
      console.warn('No se pudieron enviar los avisos de reclamos', error);
  } catch (error) {
    console.warn('No se pudieron enviar los avisos de reclamos', error);
  }
}
export async function getCaseDocumentUrl(
  path: string,
  name: string,
  download = true
): Promise<string> {
  const { data, error } = await supabase.storage
    .from('shipment-case-documents')
    .createSignedUrl(path, 300, download ? { download: name } : {});
  if (error) throw error;
  return data.signedUrl;
}
